import { Component, Input, ChangeDetectorRef, NgZone, ViewEncapsulation } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { of } from 'rxjs';
import { Observer } from 'rxjs/Observer';
import {
  NavController,
  IonSlides,
  IonSlide,
  Platform,
  ModalController,
  AlertController,
  LoadingController,
} from '@ionic/angular';
import { IntervalObservable } from 'rxjs/observable/IntervalObservable';
import { mergeMap, distinctUntilChanged } from 'rxjs/operators';

import * as moment from 'moment';
import { ScrollSpyService } from 'ngx-scrollspy';
import {
  mapStates,
  AppState,
  AuthService,
  MyPenmateService,
  EventService,
  NativeClientService,
  LetterService,
  SearchService,
} from '../services';
import { MyPenmateActions, UserActions, CreateMessageActions } from '../actions';
import { checkoutRecoveryEnabled } from '../react-messenger/checkout-recovery-lever';

import { ViewChild, ViewChildren } from '@angular/core';
import { sample, orderBy, findIndex, find, get } from 'lodash';
import { Store } from '@ngrx/store';
import { IonContent } from '@ionic/angular';
// import {
//   AddPenmatePage,
//   LoginPage,
//   MessageIndexPage,
//   MessageCreatorPage,
//   PostcardCreatorPage,
//   PenmateProfile,
//   LoadingModal,
// } from '../pages';

import { AddPenmatePage } from '../add-penmate/add-penmate';
import { LoginPage } from '../login/login.page';
import { PenmateProfile } from '../penmate-profile/penmate-profile';
import { LoadingModal } from '../loading-modal/loading-modal';
import { EmailMessagesModalPage } from '../email-messages-modal/email-messages-modal';

declare var Beacon;

/**
 * Returns absolute element top
 */
function getElementTop(el: HTMLElement) {
  return el.getBoundingClientRect().top;
}

@Component({
  selector: 'pm-page-my-penmates',
  templateUrl: 'my-penmates.html',
  styleUrls: ['./my-penmates.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MyPenmatesPage {
  @ViewChild(IonContent, { static: false }) content: IonContent;
  @ViewChildren('pmContact') pmContactEl;
  public pageHeight;
  public pageWidth;
  public modal;
  public penmate;
  public penmates = [];
  public emailMessages = [];
  public drafts = [];
  public waypointHit;
  public currentMessage;
  public pendingEmailMessage;
  public waypoints: { index: number; position: number }[] = [];
  public isNative;
  public emailMessagesModal;
  public pendingDraft;
  public user;

  constructor(
    private platform: Platform,
    public navCtrl: NavController,
    public modalCtrl: ModalController,
    public alertCtrl: AlertController,
    public loadingCtrl: LoadingController,
    private myPenmateActions: MyPenmateActions,
    private myPenmateService: MyPenmateService,
    private userActions: UserActions,
    private createMessageActions: CreateMessageActions,
    private store: Store<AppState>,
    private letterService: LetterService,
    private authService: AuthService,
    private eventService: EventService,
    private nativeService: NativeClientService,
    private scrollSpy: ScrollSpyService,
    private changeDetectorRef: ChangeDetectorRef,
    private zone: NgZone,
    private searchService: SearchService,
  ) {
    this.pageHeight = platform.height();
    this.pageWidth = platform.width();

    this.authService.authenticate().subscribe(({ isAuthenticated, user }) => {
      if (!isAuthenticated || !user) {
        return this.navCtrl.navigateForward('/login');
      }
      if (!!isAuthenticated && user) {
        this.user = user;
        this.eventService.gaUserData(user.profile_hash)
      }
    });
  }

  setupWaypoints() {
    this.pmContactEl.changes.subscribe(t => {
      for (const pmEl of t._results) {
        const position = pmEl.nativeElement.getBoundingClientRect().top;
        const index = pmEl.nativeElement.getAttribute('data-index');
        this.registerWaypoint(parseInt(index, 10), position);
      }
      this.waypointHit = this.getScrollSpy('list-scroller');
      this.waypointHit.subscribe(index => {
        const penmates = this.penmates;
        const item = penmates[index];
        if (!item.showMap) {
          penmates[index] = Object.assign({}, { ...item, showMap: true });
          this.penmates = Object.assign([], penmates);
        }
      });
    });
  }

  // ---- Passive linked-search status (the add flow records the association).
  // Never a question, never tappable: "Location verified" once the search has
  // a strong result for a match the user confirmed, and nothing at all before
  // then or otherwise — the badge only ever appears as good news, and never
  // for a person the user declined or an add they made on their own.
  private static SEARCH_ASSOC_KEY = 'pmreact.search.assoc.v1';
  private static SEARCH_POLL_MS = 10000;
  private static SEARCH_POLL_CEILING_MS = 120000;
  private static SEARCH_ASSOC_MAX_AGE_MS = 48 * 60 * 60 * 1000;
  public searchStatusPenmateId: number | null = null;
  private searchStatusSearchId: number | null = null;
  public searchStatusLabel: 'verified' | null = null;
  private searchPollTimer: any = null;
  private searchPollStartedAt = 0;
  private searchViewActive = false;
  private searchFetchGen = 0;

  searchStatusFor(penmate): 'verified' | null {
    if (!penmate || penmate.id !== this.searchStatusPenmateId) return null;
    return this.searchStatusLabel;
  }

  private readSearchAssoc(): { searchId: number; penmateId: number; matched: boolean } | null {
    try {
      const raw = window.localStorage.getItem(MyPenmatesPage.SEARCH_ASSOC_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.searchId === 'number' && typeof parsed.penmateId === 'number') {
        // A search that never reaches a terminal status must not pin the
        // card's status row forever.
        if (
          typeof parsed.at === 'number' &&
          Date.now() - parsed.at > MyPenmatesPage.SEARCH_ASSOC_MAX_AGE_MS
        ) {
          this.clearSearchAssoc();
          return null;
        }
        return { searchId: parsed.searchId, penmateId: parsed.penmateId, matched: parsed.matched === true };
      }
    } catch (e) {
      // ignore
    }
    return null;
  }

  private clearSearchAssoc() {
    try {
      window.localStorage.removeItem(MyPenmatesPage.SEARCH_ASSOC_KEY);
    } catch (e) {
      // ignore
    }
    this.searchStatusPenmateId = null;
    this.searchStatusSearchId = null;
    this.searchStatusLabel = null;
    this.stopSearchPoll();
  }

  private stopSearchPoll() {
    // Invalidate any in-flight fetch so a late response can't repaint state.
    this.searchFetchGen++;
    if (this.searchPollTimer) {
      clearInterval(this.searchPollTimer);
      this.searchPollTimer = null;
    }
  }

  private updateSearchStatusView() {
    if (!this.changeDetectorRef['destroyed']) {
      this.changeDetectorRef.detectChanges();
    }
  }

  private checkLinkedSearch(penmates) {
    // The penmates fetch can resolve after the user has already left the
    // page — starting a poll then would leak it.
    if (!this.searchViewActive) return;
    const assoc = this.readSearchAssoc();
    if (!assoc) {
      this.searchStatusPenmateId = null;
      this.searchStatusLabel = null;
      return;
    }
    // The association is a single unscoped local record — only honor it for a
    // contact that actually belongs to this user's list.
    const owned = (penmates || []).some(p => p && p.id === assoc.penmateId);
    if (!owned) {
      this.clearSearchAssoc();
      return;
    }
    // An add without a confirmed match has nothing this card can ever show.
    if (!assoc.matched) {
      this.clearSearchAssoc();
      return;
    }
    // A different contact, or a new search for the same contact, starts clean
    // (this page instance is reused across visits).
    if (assoc.penmateId !== this.searchStatusPenmateId || assoc.searchId !== this.searchStatusSearchId) {
      this.searchStatusLabel = null;
    }
    this.searchStatusPenmateId = assoc.penmateId;
    this.searchStatusSearchId = assoc.searchId;
    this.searchPollStartedAt = Date.now();
    this.stopSearchPoll();
    this.fetchLinkedSearch(assoc);
    this.searchPollTimer = setInterval(() => {
      if (Date.now() - this.searchPollStartedAt > MyPenmatesPage.SEARCH_POLL_CEILING_MS) {
        this.stopSearchPoll();
        return;
      }
      this.fetchLinkedSearch(assoc);
    }, MyPenmatesPage.SEARCH_POLL_MS);
  }

  private fetchLinkedSearch(assoc: { searchId: number; penmateId: number }) {
    // Latest fetch wins: a slower older response, or one landing after the
    // user left the page, must not overwrite newer state.
    const gen = ++this.searchFetchGen;
    this.searchService.getSearch(assoc.searchId).subscribe(
      (res: any) => {
        if (gen !== this.searchFetchGen) return;
        const search = res && res.search ? res.search : null;
        if (!search) {
          this.clearSearchAssoc();
          this.updateSearchStatusView();
          return;
        }
        const attempts = search.attempts || [];
        // The one verified signal: the confirmed match's facility resolved to
        // a deliverable mailing address. Revisit when the VINE internal tool's
        // write path lands (a human-confirmed VINE result should set this or
        // carry its own confirmation marker; raw VINE matches must not count).
        const verified = attempts.some(a => a && a.resolved_facility_address_id != null);
        const terminal = search.status === 'complete' || search.status === 'failed';
        if (verified) {
          this.searchStatusLabel = 'verified';
          this.stopSearchPoll();
          this.updateSearchStatusView();
        } else if (terminal) {
          // Finished with nothing to show — quietly nothing, and stop looking.
          this.clearSearchAssoc();
          this.updateSearchStatusView();
        }
      },
      err => {
        if (gen !== this.searchFetchGen) return;
        // Gone or not ours (404) — quietly drop it. Anything transient
        // (offline, 5xx) keeps the association; the poll retries this visit.
        if (err && err.status === 404) {
          this.clearSearchAssoc();
          this.updateSearchStatusView();
        }
      },
    );
  }

  ionViewWillLeave() {
    this.searchViewActive = false;
    this.stopSearchPoll();
  }

  ngOnDestroy() {
    this.stopSearchPoll();
  }

  ionViewDidEnter() {
    this.recoveryCtaTracked = {};
    this.searchViewActive = true;
    this.isNative = !!this.nativeService.active;
    this.myPenmateService.getPenmates().subscribe(penmates => {
      this.store.dispatch(this.myPenmateActions.loadPenmatesSuccess(penmates));
      if (penmates.length === 0) {
        this.penmates = penmates;
        return this.navCtrl.navigateForward('/find-an-inmate');
      }

      this.setupWaypoints();
      this.store.dispatch(this.createMessageActions.fetchDrafts());
      this.checkLinkedSearch(penmates);

      this.store
        .select(state => state.user.emailMessages)
        .subscribe(emailMessages => {
          this.emailMessages = emailMessages.map(message =>
            Object.assign({}, message, { message: message['body'], emailMessage: true }),
          );
        });

      this.store
        .select(state => state.createMessage)
        .subscribe(currentMessage => {
          this.currentMessage = currentMessage;
        });

      this.store
        .select(state => state.myPenmates.drafts)
        .subscribe(drafts => {
          this.drafts = drafts;
        });

      this.store
        .select(state => state.user.pendingEmailMessage)
        .subscribe(pendingEmailMessage => {
          this.pendingEmailMessage = pendingEmailMessage;
        });

      this.store
        .select(state => state.myPenmates.penmates)
        .subscribe(myPenmates => {
          if (myPenmates && myPenmates.length > 0) {
            this.penmates = orderBy(myPenmates, 'id', 'desc');
            // Maps are initialized on scroll. We'll autoload the first two..
            //
            if (this.penmates[0]) {
              this.penmates[0].showMap = true;
            }
            if (this.penmates[1]) {
              this.penmates[1].showMap = true;
            }

            if (!this.changeDetectorRef['destroyed']) {
              this.changeDetectorRef.detectChanges(); // TODO: force detectCahanges? why?
            }
          }
        });
    });
    Beacon('destroy');
  }

  onViewMessages(penmate) {
    this.store.dispatch(this.myPenmateActions.viewPenmate(penmate));
    this.navCtrl.navigateForward(`/penmates/${penmate.id}/messenger`);
    this.eventService.track('view-messages', { penmate_id: penmate.id });
  }

  onEditDraft = async (draft, penmate) => {
    if (penmate && penmate.id) {
      this.store.dispatch(this.myPenmateActions.viewPenmate(penmate));
    }
    if (draft.emailMessage) {
      return this.onEditEmailDraft(draft, penmate);
    }
    const draftData = get(draft, 'data', {});
    const rawMessageType = String(get(draftData, 'messageType') || get(draft, 'order_type') || '').toLowerCase();
    const isPostcard = rawMessageType === 'postcard';
    const rawShippingMethod = String(get(draftData, 'shippingMethod') || get(draft, 'shipment_method') || '').toLowerCase();
    const routeMessageType = rawShippingMethod === 'emessage' ? 'emessage' : isPostcard ? 'postcard' : 'letter';
    this.eventService.track('edit-draft', {
      letter_id: draft.id,
      message_type: isPostcard ? 'postcard' : 'message',
      penmate_id: penmate.id,
    });
    // Checkout recovery return surface: the offer row deep-links into checkout
    // with a flag the messenger consumes to present the offer sheet on arrival.
    if (this.recoveryOfferFor(draft, penmate)) {
      this.eventService.track('checkout-recovery-cta-tapped', {
        letter_id: draft.id,
        penmate_id: penmate.id,
        has_issuance: !!(draft.recovery_offer && draft.recovery_offer.issuance_id),
      });
      this.navCtrl.navigateForward(
        `/penmates/${penmate.id}/messenger?messageType=${encodeURIComponent(routeMessageType)}` +
          `&letterId=${encodeURIComponent(String(draft.id))}&recoveryOffer=${encodeURIComponent(String(draft.id))}`,
      );
      return;
    }
    this.navCtrl.navigateForward(
      `/penmates/${penmate.id}/messenger?messageType=${encodeURIComponent(routeMessageType)}`,
    );
  };

  requiresIdVerification(penmate) {
    if (
      !penmate ||
      !this.user ||
      !this.user.id_verification_required ||
      !this.user.id_verification_pending_review ||
      !this.user.id_verification_recipient
    ) {
      return false;
    }
    if (this.user.id_verification_completed) {
      return false;
    }
    const recipientId = this.user.id_verification_recipient.id || null
    return recipientId && penmate.id && penmate.id === recipientId
  }

  onMyAccount() {
    this.navCtrl.navigateForward('/my-account');
  }

  onEditEmailDraft = async (draft, penmate) => {
    const loadingModal = await this.modalCtrl.create({
      component: LoadingModal,
      componentProps: { message: 'Loading letter...' },
    });
    await loadingModal.present();

    this.letterService.updateEmailMessage(draft.id, { createLetter: true }).subscribe(async res => {
      await loadingModal.dismiss();
      const letter = get(res, 'letter', {});
      const letterPenmate = get(res, 'penmate');
      this.store.dispatch(this.userActions.setPendingEmailMessage(letter));
      if (letterPenmate) {
        this.store.dispatch(this.myPenmateActions.viewPenmate(letterPenmate));
        this.store.dispatch(this.createMessageActions.setMessage(letter));
        this.navCtrl.navigateForward(`/penmates/${letterPenmate.id}/new-message`);
        this.store.dispatch(this.myPenmateActions.loadPenmates());
        this.eventService.track('email-message-create', {
          letter_id: letterPenmate.id,
          penmate_id: letterPenmate.id,
        });
      }
    });
    return;
  };

  onExploreMedia(penmate) {
    this.nativeService.sendMessage({ type: 'SHOW_LIBRARY', payload: { penmate } });
  }

  onSendEMessage(penmate) {
    this.store.dispatch(this.myPenmateActions.viewPenmate(penmate));
    this.navCtrl.navigateForward(`/penmates/${penmate.id}/messenger?messageType=emessage`);
    this.eventService.track('create-letter', { penmate_id: penmate.id, shipping_method: 'emessage' });
  }

  onCreateMessage(penmate, eMessage = null) {
    this.store.dispatch(this.myPenmateActions.viewPenmate(penmate));
    this.navCtrl.navigateForward(`/penmates/${penmate.id}/messenger?messageType=letter`);
    this.eventService.track('create-letter', { penmate_id: penmate.id, eMessage });
  }

  onCreatePostcard(penmate) {
    this.store.dispatch(this.myPenmateActions.viewPenmate(penmate));
    this.navCtrl.navigateForward(`/penmates/${penmate.id}/messenger?messageType=postcard`);
    this.eventService.track('create-postcard', { penmate_id: penmate.id });
  }

  onRemovePenmate = async penmate => {
    const cancelButton = {
      text: 'Cancel',
      role: 'cancel',
      cssClass: 'alert-button',
      handler: () => {},
    };

    const delButton = {
      text: 'Yes',
      cssClass: 'delete-button',
      handler: () => {
        this.removePenmate(penmate.id);
      },
    };

    const confirm = await this.alertCtrl.create({
      header: 'Are you sure you want to remove this Penmate?',
      cssClass: 'alert-confirm',
      message: '<span class="alert-text-confirm">Important: This will remove all of your messages for this person.</span>',
      buttons: [cancelButton, delButton],
    });
    await confirm.present();
  };

  removePenmate(id) {
    this.myPenmateService.removePenmate(id).subscribe(res => {
      this.store.dispatch(this.myPenmateActions.removePenmateSuccess(res));
      this.store.dispatch(this.myPenmateActions.loadPenmates());
    });
  }

  // Checkout recovery "return" surface: the pending-draft row becomes the
  // offer CTA when the draft carries an open/eligible offer (server-computed
  // per draft; null for holdouts/ineligible). Copy is server-owned: a payload
  // missing either line renders nothing. Honours the client kill lever.
  private recoveryCtaTracked: { [letterId: string]: boolean } = {};

  recoveryOfferFor = (draft, penmate?): { cta_title: string; cta_subtitle: string } | null => {
    const offer = draft && draft.recovery_offer;
    if (!offer || typeof offer !== 'object') return null;
    if (!this.checkoutRecoveryEnabled()) return null;
    if (!offer.cta_title || !offer.cta_subtitle) return null;
    const draftData = get(draft, 'data', {});
    const messageType = String(get(draftData, 'messageType') || get(draft, 'order_type') || '').toLowerCase();
    if (messageType === 'postcard') return null;
    this.trackRecoveryOfferCta(draft, penmate, offer);
    return { cta_title: String(offer.cta_title), cta_subtitle: String(offer.cta_subtitle) };
  };

  private checkoutRecoveryEnabled(): boolean {
    return checkoutRecoveryEnabled();
  }

  // One impression event per rendered offer row per view entry.
  private trackRecoveryOfferCta(draft, penmate, offer): void {
    if (!draft || !draft.id || this.recoveryCtaTracked[String(draft.id)]) return;
    this.recoveryCtaTracked[String(draft.id)] = true;
    this.eventService.track('checkout-recovery-cta-displayed', {
      letter_id: draft.id,
      penmate_id: penmate && penmate.id ? penmate.id : null,
      has_issuance: !!offer.issuance_id,
    });
  }

  getPendingDraft = penmate => {
    if (this.emailMessages.length > 0) {
      return find(this.emailMessages, draft => get(draft, 'inmate.id') === get(penmate, 'id'));
    }
    try {
      const drafts = orderBy(this.drafts, (d) => new Date(d.created_at).getTime(), 'desc')
      return find(drafts, draft => get(draft, 'recipient.id') === get(penmate, 'id'));
    } catch {
      const drafts = orderBy(this.drafts, 'id', 'desc');
      return find(drafts, draft => get(draft, 'recipient.id') === get(penmate, 'id'));
    }
  };

  private getScrollSpy(id) {
    return this.scrollSpy
      .getObservable(id)
      .pipe(
        mergeMap((e: any) => {
          const pos = e.target.scrollTop + window.outerHeight - 80;
          if (pos) {
            const elemId = this.getWaypointAtPosition(pos);
            if (elemId) {
              return of(elemId);
            }
            return Observable.empty();
          }
          return Observable.empty();
        }),
      )
      .pipe(distinctUntilChanged());
  }

  private registerWaypoint(index: number, position: number) {
    this.waypoints.push({ index, position });
  }

  private getWaypointAtPosition(scrollPos: number): string {
    let id = null;
    let lastDistance = Infinity; //smallest, positive distance
    for (let entry of this.waypoints) {
      let distance = scrollPos - entry.position + 10;
      if (distance > 0 && distance < lastDistance) {
        id = entry.index;
        lastDistance = distance;
      }
    }
    return id;
  }
}
